import type React from 'react'
import { useIntl } from 'react-intl'

import { Navigate, useNavigate, useSearch } from '@tanstack/react-router'

import { useFirebaseUser } from '@/auth/useFirebaseUser'
import {
  AllCentralsConfigurationHistoryView,
  ConfigurationHistoryView,
  type StatsSection,
} from '@/components/templates/ConfigurationHistoryView'

import { useAllCentralsConfigurationHistoryPage } from './useAllCentralsConfigurationHistoryPage'
import { useClosestAssignments } from './useClosestAssignments'
import { useConfigurationHistoryPage } from './useConfigurationHistoryPage'

// Special value for "all centrals" mode
export const ALL_CENTRALS_REGION_ID = 'all'

// Defer navigation to allow dropdown animation to complete before React suspends
const deferNavigation = (doNavigate: () => void) => {
  requestAnimationFrame(() => {
    setTimeout(doNavigate, 50)
  })
}

// Inner component for single-central view
const SingleCentralView: React.FC<{
  regionId: string | undefined
  operatingDate: string | undefined
  onRegionChange: (regionId: string | undefined) => void
  onDateChange: (operatingDate: string | undefined) => void
  onSectionChange: (section: StatsSection) => void
  onBackToPiloteClicked: () => void
}> = ({ regionId, operatingDate, onRegionChange, onDateChange, onSectionChange, onBackToPiloteClicked }) => {
  const intl = useIntl()

  const { availableRegions, availableDates, stats, eveningStartTime, eveningEndTime, events, isLoading } =
    useConfigurationHistoryPage({
      regionId,
      operatingDate,
      locale: intl.locale,
      nowLabel: intl.formatMessage({ id: 'stats.now' }),
    })

  const { closestAssignments, isLoading: isLoadingAssignments } = useClosestAssignments(events, regionId)

  // Add "All centrals" option at the top
  const regionsWithAllOption = [
    { regionId: ALL_CENTRALS_REGION_ID, regionName: intl.formatMessage({ id: 'configurationHistory.allCentrals' }) },
    ...availableRegions,
  ]

  return (
    <ConfigurationHistoryView
      availableRegions={regionsWithAllOption}
      availableDates={availableDates}
      selectedRegionId={regionId}
      selectedOperatingDate={operatingDate}
      stats={stats}
      eveningStartTime={eveningStartTime}
      eveningEndTime={eveningEndTime}
      events={events}
      isLoading={isLoading}
      closestAssignments={closestAssignments}
      isLoadingAssignments={isLoadingAssignments}
      selectedSection="configuration"
      onSectionChange={onSectionChange}
      onRegionChange={onRegionChange}
      onDateChange={onDateChange}
      onBackToPiloteClicked={onBackToPiloteClicked}
    />
  )
}

// Inner component for all-centrals view
const AllCentralsView: React.FC<{
  operatingDate: string | undefined
  onRegionChange: (regionId: string | undefined) => void
  onDateChange: (operatingDate: string | undefined) => void
  onSectionChange: (section: StatsSection) => void
  onBackToPiloteClicked: () => void
}> = ({ operatingDate, onRegionChange, onDateChange, onSectionChange, onBackToPiloteClicked }) => {
  const intl = useIntl()

  const { availableDates, stats, events, regionNames, eveningStartTime, eveningEndTime, isLoading, isLive, error } =
    useAllCentralsConfigurationHistoryPage({
      operatingDate,
      locale: intl.locale,
      nowLabel: intl.formatMessage({ id: 'stats.now' }),
    })

  // Build region options including "All centrals" at the top
  const regionsWithAllOption = [
    { regionId: ALL_CENTRALS_REGION_ID, regionName: intl.formatMessage({ id: 'configurationHistory.allCentrals' }) },
    ...Object.entries(regionNames)
      .map(([id, name]) => ({ regionId: id, regionName: name }))
      .sort((a, b) => a.regionName.localeCompare(b.regionName)),
  ]

  return (
    <AllCentralsConfigurationHistoryView
      availableRegions={regionsWithAllOption}
      availableDates={availableDates}
      selectedOperatingDate={operatingDate}
      stats={stats}
      events={events}
      regionNames={regionNames}
      eveningStartTime={eveningStartTime}
      eveningEndTime={eveningEndTime}
      isLoading={isLoading}
      isLive={isLive}
      error={error}
      selectedSection="configuration"
      onSectionChange={onSectionChange}
      onRegionChange={onRegionChange}
      onDateChange={onDateChange}
      onBackToPiloteClicked={onBackToPiloteClicked}
    />
  )
}

export const ConfigurationHistoryPage: React.FC = () => {
  const user = useFirebaseUser()
  const { regionId, operatingDate } = useSearch({ from: '/stats/' })
  const navigate = useNavigate()

  if (!user?.sys_admin) {
    return <Navigate to="/" search={{ central_id: undefined }} replace />
  }

  const handleRegionChange = (newRegionId: string | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: {
          section: 'configuration',
          regionId: newRegionId,
          operatingDate: undefined, // Clear date when region changes
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleDateChange = (newOperatingDate: string | undefined) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: {
          section: 'configuration',
          regionId,
          operatingDate: newOperatingDate,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleSectionChange = (newSection: StatsSection) => {
    deferNavigation(() => {
      navigate({
        to: '/stats',
        search: {
          section: newSection,
          regionId,
          // Clear configuration-specific params when switching to requests
          operatingDate: newSection === 'configuration' ? operatingDate : undefined,
        },
      }).catch(() => {
        // Navigation errors are handled by the router
      })
    })
  }

  const handleBackToPilote = async () => {
    await navigate({ to: '/', search: { central_id: undefined } })
  }

  // Render all-centrals view or single-central view based on regionId
  if (regionId === ALL_CENTRALS_REGION_ID) {
    return (
      <AllCentralsView
        operatingDate={operatingDate}
        onRegionChange={handleRegionChange}
        onDateChange={handleDateChange}
        onSectionChange={handleSectionChange}
        onBackToPiloteClicked={handleBackToPilote}
      />
    )
  }

  return (
    <SingleCentralView
      regionId={regionId}
      operatingDate={operatingDate}
      onRegionChange={handleRegionChange}
      onDateChange={handleDateChange}
      onSectionChange={handleSectionChange}
      onBackToPiloteClicked={handleBackToPilote}
    />
  )
}
